import { useEffect, useState } from "react";
import { Crown, ShoppingBag, ArrowRight, Plus, Minus, X, Check, Copy } from "lucide-react";
import { api, BRAND } from "../lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";

const CATEGORIES = [
  { v: "all", label: "All" },
  { v: "program", label: "Program" },
  { v: "book", label: "Books" },
  { v: "apparel", label: "Apparel" },
];

export default function Blueprint() {
  const [products, setProducts] = useState([]);
  const [cat, setCat] = useState("all");
  const [cart, setCart] = useState(() => {
    try { return JSON.parse(localStorage.getItem("cart") || "[]"); } catch { return []; }
  });
  const [cartOpen, setCartOpen] = useState(false);
  const [enrollOpen, setEnrollOpen] = useState(false);

  // Enrollment form
  const [eForm, setEForm] = useState({ name: "", email: "", phone: "", payment_method: "card", notes: "" });
  const [eBusy, setEBusy] = useState(false);
  const [eDone, setEDone] = useState(null);

  // Checkout
  const [cForm, setCForm] = useState({ customer_name: "", customer_email: "", payment_method: "card", notes: "" });
  const [cBusy, setCBusy] = useState(false);
  const [cDone, setCDone] = useState(null);

  useEffect(() => {
    api.get("/products").then((r)=>setProducts(r.data || [])).catch(()=>{});
  }, []);

  useEffect(() => {
    localStorage.setItem("cart", JSON.stringify(cart));
  }, [cart]);

  const filtered = products.filter(p => cat === "all" || p.category === cat);

  const addToCart = (p) => {
    setCart((arr) => {
      const ex = arr.find(i => i.id === p.id);
      if (ex) return arr.map(i => i.id === p.id ? { ...i, qty: i.qty + 1 } : i);
      return [...arr, { id: p.id, title: p.title, price: p.price, image_url: p.image_url, qty: 1 }];
    });
    toast.success(`Added: ${p.title}`);
  };
  const inc = (id) => setCart(arr => arr.map(i => i.id === id ? { ...i, qty: i.qty + 1 } : i));
  const dec = (id) => setCart(arr => arr.flatMap(i => i.id === id ? (i.qty > 1 ? [{ ...i, qty: i.qty - 1 }] : []) : [i]));
  const removeItem = (id) => setCart(arr => arr.filter(i => i.id !== id));

  const total = cart.reduce((s, i) => s + i.price * i.qty, 0);

  const enroll = async (e) => {
    e.preventDefault();
    if (!eForm.name || !eForm.email) return toast.error("Name and email required");
    setEBusy(true);
    try {
      if (eForm.payment_method === "card") {
        const r = await api.post("/stripe/create-session", {
          origin_url: window.location.origin,
          kind: "enrollment",
          tier: "material",
          name: eForm.name, email: eForm.email, phone: eForm.phone, notes: eForm.notes,
        });
        if (r.data?.url) { window.location.href = r.data.url; return; }
        toast.error("Could not start card checkout.");
      } else {
        const r = await api.post("/enrollments", eForm);
        setEDone(r.data);
        toast.success("You're enrolled — check your email!");
      }
    } catch { toast.error("Could not enroll right now."); } finally { setEBusy(false); }
  };

  const checkout = async (e) => {
    e.preventDefault();
    if (!cForm.customer_name || !cForm.customer_email) return toast.error("Name and email required");
    if (cart.length === 0) return toast.error("Your cart is empty");
    setCBusy(true);
    try {
      if (cForm.payment_method === "card") {
        const r = await api.post("/stripe/create-session", {
          origin_url: window.location.origin,
          kind: "order",
          items: cart.map(i => ({ id: i.id, qty: i.qty })),
          customer_name: cForm.customer_name, customer_email: cForm.customer_email,
        });
        if (r.data?.url) { window.location.href = r.data.url; return; }
        toast.error("Could not start card checkout.");
      } else {
        const r = await api.post("/checkout", {
          items: cart.map(i => ({ id: i.id, title: i.title, price: i.price, qty: i.qty })),
          total, ...cForm,
        });
        setCDone(r.data);
        setCart([]);
        toast.success("Order placed — payment instructions sent!");
      }
    } catch { toast.error("Could not place order"); } finally { setCBusy(false); }
  };

  const copy = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied!");
  };

  return (
    <div data-testid="page-blueprint">
      {/* Floating cart button */}
      <button
        data-testid="cart-button"
        onClick={() => setCartOpen(true)}
        className="fixed bottom-6 right-6 z-40 btn-gold !rounded-full !w-16 !h-16 !p-0 shadow-2xl"
      >
        <ShoppingBag className="w-6 h-6" />
        {cart.length > 0 && (
          <span className="absolute -top-1 -right-1 bg-[#D91668] text-white text-xs rounded-full w-6 h-6 flex items-center justify-center font-bold">
            {cart.reduce((s,i)=>s+i.qty,0)}
          </span>
        )}
      </button>

      {/* HERO */}
      <section className="container-narrow pt-16 pb-10">
        <div className="label-luxe">Program & Store</div>
        <h1 className="font-serif text-5xl md:text-7xl leading-[0.95] mt-4 max-w-4xl">
          Move <span className="italic gold-text">forward</span> with God.
        </h1>
      </section>

      {/* BLUEPRINT FEATURE */}
      <section className="container-narrow py-12">
        <div className="rounded-[32px] border border-[#D4AF37]/30 overflow-hidden relative card-dark">
          <div className="absolute inset-0 hero-grid opacity-30" />
          <div className="relative grid lg:grid-cols-2 gap-12 p-10 md:p-16 items-center">
            <div className="order-2 lg:order-1">
              <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#D4AF37]">Healing Crowns Presents</div>
              <h2 className="font-serif text-4xl md:text-5xl mt-3 leading-tight text-white">
                The <span className="gold-text italic">Next Step</span><br/>
                <span className="fuchsia-text italic">Blueprint™</span>
              </h2>
              <p className="text-sm uppercase tracking-[0.3em] mt-3 text-[#D4AF37]">Clarity · Alignment · Action</p>
              <p className="mt-6 text-white/90 text-lg leading-relaxed">
                A faith-based system designed to help you break cycles, hear God clearly, and move
                with purpose into your next chapter.
              </p>
              <ul className="mt-6 space-y-3 text-white/90">
                {[
                  "Identify the cycle that's been keeping you stuck",
                  "Realign your daily walk with the Holy Spirit",
                  "Activate practical action steps with accountability",
                  "Walk in your God-given assignment with confidence",
                ].map((x,i)=>(
                  <li key={i} className="flex items-start gap-3"><Crown className="w-5 h-5 text-[#D4AF37] mt-0.5"/>{x}</li>
                ))}
              </ul>
              <div className="mt-8 flex flex-wrap gap-3">
                <button onClick={() => setEnrollOpen(true)} data-testid="blueprint-enroll-btn" className="btn-fuchsia">Enroll Now <ArrowRight className="w-4 h-4"/></button>
                <a href="#store" className="btn-gold">Or Shop The Store</a>
              </div>
            </div>
            <div className="order-1 lg:order-2 relative">
              <img src={BRAND.logoBlueprint} alt="Next Step Blueprint" className="w-full max-w-md mx-auto rounded-3xl shadow-2xl"/>
            </div>
          </div>
        </div>
      </section>

      {/* STORE */}
      <section id="store" className="container-narrow py-20">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div>
            <div className="label-luxe">The Store</div>
            <h2 className="font-serif text-4xl mt-2">Branded essentials for the <span className="fuchsia-text italic">surrendered</span>.</h2>
          </div>
          <Tabs value={cat} onValueChange={setCat}>
            <TabsList className="bg-white/5 border border-white/10">
              {CATEGORIES.map(c => (
                <TabsTrigger key={c.v} value={c.v} data-testid={`cat-${c.v}`} className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#050814]">
                  {c.label}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {filtered.map((p) => (
            <div key={p.id} className="card-luxe group" data-testid={`product-${p.id}`}>
              <div className="aspect-square rounded-2xl overflow-hidden mb-4 relative bg-white/5 flex items-center justify-center p-3">
                <img src={p.image_url || "https://images.unsplash.com/photo-1544716278-ca5e3f4abd8c?auto=format&fit=crop&w=900&q=70"}
                     alt={p.title} className="w-full h-full object-contain group-hover:scale-[1.03] transition duration-500"/>
                {p.badge && (
                  <div className="absolute top-3 left-3 px-3 py-1 rounded-full bg-[#D4AF37] text-[#050814] text-[10px] tracking-[0.2em] uppercase font-bold">
                    {p.badge}
                  </div>
                )}
              </div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#F02078]">{p.category}</div>
              <h3 className="font-serif text-xl mt-1">{p.title}</h3>
              <p className="text-white/90 text-sm mt-2 leading-relaxed">{p.description}</p>
              <div className="mt-4 flex items-center justify-between">
                <div className="font-serif text-2xl text-[#ECC23D]">${p.price.toFixed(2)}</div>
                <button onClick={() => addToCart(p)} data-testid={`add-${p.id}`} className="btn-gold !py-2 !px-4 text-sm">
                  <Plus className="w-4 h-4"/> Add
                </button>
              </div>
            </div>
          ))}
        </div>
        {filtered.length === 0 && (
          <div className="card-luxe text-center text-white/80 py-16">No products in this category yet.</div>
        )}
      </section>

      {/* CART DIALOG */}
      <Dialog open={cartOpen} onOpenChange={setCartOpen}>
        <DialogContent className="max-w-2xl bg-[#000099] border border-white/10 text-white max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif text-3xl">Your Cart</DialogTitle>
            <DialogDescription className="text-white/80">Review your items and complete payment via Zelle or Cash App.</DialogDescription>
          </DialogHeader>
          {cDone ? (
            <PaymentInstructions order={cDone} cashapp={BRAND.cashapp} zelle={BRAND.zelle} onClose={() => { setCartOpen(false); setCDone(null); }} copy={copy} />
          ) : cart.length === 0 ? (
            <div className="text-white/80 py-10 text-center">Your cart is empty. The next breakthrough is waiting.</div>
          ) : (
            <>
              <div className="space-y-3">
                {cart.map(i => (
                  <div key={i.id} className="flex items-center gap-3 p-3 rounded-xl bg-white/[0.04] border border-white/10">
                    <img src={i.image_url} alt={i.title} className="w-14 h-14 rounded-lg object-cover"/>
                    <div className="flex-1">
                      <div className="font-serif text-lg">{i.title}</div>
                      <div className="text-sm text-white/80">${i.price.toFixed(2)}</div>
                    </div>
                    <div className="flex items-center gap-2 bg-white/5 rounded-full p-1 border border-white/10">
                      <button onClick={() => dec(i.id)} className="w-7 h-7 rounded-full hover:bg-white/10"><Minus className="w-3 h-3 mx-auto"/></button>
                      <span className="w-6 text-center text-sm">{i.qty}</span>
                      <button onClick={() => inc(i.id)} className="w-7 h-7 rounded-full hover:bg-white/10"><Plus className="w-3 h-3 mx-auto"/></button>
                    </div>
                    <button onClick={() => removeItem(i.id)} className="text-white/80 hover:text-[#D91668]"><X className="w-4 h-4"/></button>
                  </div>
                ))}
              </div>
              <div className="border-t border-white/10 pt-4 mt-2 flex items-center justify-between">
                <div className="text-sm text-white/80">Total</div>
                <div className="font-serif text-3xl text-[#ECC23D]">${total.toFixed(2)}</div>
              </div>
              <form onSubmit={checkout} className="space-y-3 mt-2">
                <div className="grid sm:grid-cols-2 gap-3">
                  <input data-testid="checkout-name" required placeholder="Your Name" value={cForm.customer_name} onChange={(e)=>setCForm({...cForm, customer_name:e.target.value})} className="input-luxe"/>
                  <input data-testid="checkout-email" required type="email" placeholder="Email" value={cForm.customer_email} onChange={(e)=>setCForm({...cForm, customer_email:e.target.value})} className="input-luxe"/>
                </div>
                <PaymentSelect value={cForm.payment_method} onChange={(v)=>setCForm({...cForm, payment_method:v})} />
                <button data-testid="checkout-submit" disabled={cBusy} className="btn-fuchsia w-full">
                  {cBusy ? "Processing…" : cForm.payment_method === "card" ? `Pay with Card · $${total.toFixed(2)}` : `Place Order · $${total.toFixed(2)}`}
                </button>
                <p className="text-xs text-white/80 text-center">
                  {cForm.payment_method === "card"
                    ? "Secure payment via Stripe. Visa, Mastercard, Amex, Apple Pay & Google Pay accepted."
                    : "After ordering you'll receive Zelle/Cash App payment instructions by email."}
                </p>
              </form>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* ENROLL DIALOG */}
      <Dialog open={enrollOpen} onOpenChange={setEnrollOpen}>
        <DialogContent className="max-w-lg bg-[#000099] border border-white/10 text-white max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif text-3xl">Enroll · Next Step Blueprint™</DialogTitle>
            <DialogDescription className="text-white/80">Reserve your spot — complete payment via Zelle or Cash App after submitting.</DialogDescription>
          </DialogHeader>
          {eDone ? (
            <PaymentInstructions order={eDone} cashapp={BRAND.cashapp} zelle={BRAND.zelle} onClose={() => { setEnrollOpen(false); setEDone(null); }} copy={copy} label="Enrollment"/>
          ) : (
            <form onSubmit={enroll} className="space-y-3 mt-2">
              <input data-testid="enroll-name" required placeholder="Full Name" value={eForm.name} onChange={(e)=>setEForm({...eForm, name:e.target.value})} className="input-luxe"/>
              <input data-testid="enroll-email" required type="email" placeholder="Email" value={eForm.email} onChange={(e)=>setEForm({...eForm, email:e.target.value})} className="input-luxe"/>
              <input data-testid="enroll-phone" placeholder="Phone (optional)" value={eForm.phone} onChange={(e)=>setEForm({...eForm, phone:e.target.value})} className="input-luxe"/>
              <PaymentSelect value={eForm.payment_method} onChange={(v)=>setEForm({...eForm, payment_method:v})}/>
              <textarea data-testid="enroll-notes" placeholder="Anything you'd like Pastor Shalonda to know…" rows={3} value={eForm.notes} onChange={(e)=>setEForm({...eForm, notes:e.target.value})} className="input-luxe"/>
              <button data-testid="enroll-submit" disabled={eBusy} className="btn-gold w-full">
                <Crown className="w-4 h-4"/> {eBusy ? "Processing…" : eForm.payment_method === "card" ? "Pay with Card · Reserve My Spot" : "Reserve My Spot"}
              </button>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PaymentSelect({ value, onChange }) {
  return (
    <div>
      <label className="label-luxe">Payment Method</label>
      <div className="grid grid-cols-3 gap-3 mt-2">
        {[
          { v: "card", label: "Card" },
          { v: "zelle", label: "Zelle" },
          { v: "cashapp", label: "Cash App" },
        ].map((x) => (
          <button key={x.v} type="button" data-testid={`pay-${x.v}`} onClick={() => onChange(x.v)}
            className={`px-3 py-3 rounded-xl border text-sm font-semibold transition ${
              value === x.v ? "border-[#D4AF37] bg-[#D4AF37]/10 text-[#ECC23D]" : "border-white/10 text-white/90 hover:border-white/30"
            }`}
          >{x.label}</button>
        ))}
      </div>
      {value === "card" && (
        <p className="text-xs text-white/70 mt-2">You'll be redirected to a secure Stripe checkout to pay by credit or debit card.</p>
      )}
    </div>
  );
}

function PaymentInstructions({ order, zelle, cashapp, onClose, copy, label = "Order" }) {
  const handle = order.payment?.handle;
  const method = order.payment?.label;
  return (
    <div data-testid="payment-instructions" className="space-y-4">
      <div className="text-center py-4">
        <div className="w-14 h-14 rounded-full bg-[#D4AF37]/20 text-[#ECC23D] flex items-center justify-center mx-auto"><Check className="w-7 h-7"/></div>
        <h3 className="font-serif text-2xl mt-3">{label} Received!</h3>
        <p className="text-white/80 text-sm mt-1">Reference: <span className="text-[#ECC23D] font-mono">{order.id?.slice(0,8)}</span></p>
      </div>
      <div className="card-luxe">
        <div className="text-sm text-white/90">Send payment via <span className="text-white font-bold">{method}</span> to:</div>
        <div className="mt-3 flex items-center justify-between gap-3">
          <div className="font-serif text-2xl text-[#ECC23D] break-all">{handle}</div>
          <button onClick={() => copy(handle)} className="btn-ghost-gold !py-2 !px-3 text-xs"><Copy className="w-3.5 h-3.5"/> Copy</button>
        </div>
        <div className="mt-2 text-xs text-white/80">Pastor Shalonda Raymond</div>
      </div>
      <div className="text-xs text-white/80 text-center">Check your email for full instructions and reference number.</div>
      <button onClick={onClose} className="btn-fuchsia w-full">Done</button>
    </div>
  );
}
